/**
 * Toast Context - Simple toast notification system
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, options = {}) => {
    const id = Date.now() + Math.random();
    const toast = {
      id,
      message,
      type: options.type || 'info', // 'success', 'error', 'warning', 'info'
      duration: options.duration || 5000,
      ...options
    };

    setToasts(prev => [...prev, toast]);

    // Auto remove after duration
    if (toast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, toast.duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback((message, options) => {
    return addToast(message, { type: 'success', ...options });
  }, [addToast]);

  const error = useCallback((message, options) => {
    return addToast(message, { type: 'error', duration: 7000, ...options });
  }, [addToast]);

  const warning = useCallback((message, options) => {
    return addToast(message, { type: 'warning', duration: 6000, ...options });
  }, [addToast]);

  const info = useCallback((message, options) => {
    return addToast(message, { type: 'info', ...options });
  }, [addToast]);

  const value = {
    toasts,
    addToast,
    removeToast,
    success,
    error,
    warning,
    info
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

const ToastContainer = ({ toasts, onRemove }) => {
  if (toasts.length === 0) return null;

  return (
    <div style={styles.container}>
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

const Toast = ({ toast, onRemove }) => {
  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
        );
    }
  };

  const getStyles = () => {
    const base = {
      ...styles.toast,
      ...(toast.type === 'success' && styles.success),
      ...(toast.type === 'error' && styles.error),
      ...(toast.type === 'warning' && styles.warning),
      ...(toast.type === 'info' && styles.info)
    };
    return base;
  };

  return (
    <div style={getStyles()}>
      <div style={styles.icon}>
        {getIcon()}
      </div>
      <div style={styles.content}>
        {toast.message}
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        style={styles.closeButton}
        aria-label="Close toast"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>
    </div>
  );
};

const styles = {
  container: {
    position: 'fixed',
    top: '20px',
    right: '20px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    maxWidth: '400px'
  },
  toast: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    borderRadius: '4px',
    background: '#1a1a1a',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
    border: 'none',
    fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: '500',
    lineHeight: '1.5',
    color: '#ffffff',
    animation: 'slideIn 0.3s ease-out'
  },
  success: {
    background: '#1a1a1a',
    color: '#ffffff'
  },
  error: {
    background: '#1a1a1a',
    color: '#ffffff'
  },
  warning: {
    background: '#1a1a1a',
    color: '#ffffff'
  },
  info: {
    background: '#1a1a1a',
    color: '#ffffff'
  },
  icon: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center'
  },
  content: {
    flex: 1,
    minWidth: 0
  },
  closeButton: {
    background: 'none',
    border: 'none',
    padding: '2px',
    cursor: 'pointer',
    opacity: '0.7',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.2s',
    color: '#ffffff'
  }
};

// Add animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;
if (!document.head.querySelector('style[data-toast-animations]')) {
  styleSheet.setAttribute('data-toast-animations', 'true');
  document.head.appendChild(styleSheet);
}

export default ToastProvider;
